/* eslint-disable complexity */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TabNavigationProvider } from './tab-navigation-context';

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} [className]
 * @prop {'alt' | 'default'} [variant]
 * @prop {number} [defaultActiveKey]
 * @prop {'horizontal' | 'vertical'} [orientation]
 * @prop {'left' | 'right'} [lineSide]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifTabNavigation = (props) => {
  const {
    className = '',
    children,
    variant = 'default',
    defaultActiveKey = -1,
    orientation = 'horizontal',
    lineSide = 'left',
    ...other
  } = props;
  const [currentTab, setCurrentTab] = useState(defaultActiveKey > 0 ? defaultActiveKey : 0);
  const [activeTab, setActiveTab] = useState(defaultActiveKey);
  const totalTabs = React.Children.count(children);
  const buttonRefs = useRef([]);

  const classes = classNames({
    'motif-tab-navigation': true,
    'motif-tab-navigation-horizontal': orientation === 'horizontal',
    'motif-tab-navigation-vertical': orientation === 'vertical',
    'motif-tab-navigation-vertical-left': orientation === 'vertical' && lineSide === 'left',
    'motif-tab-navigation-vertical-right': orientation === 'vertical' && lineSide === 'right',
    'motif-tab-navigation-alt': variant === 'alt',
    [className]: true
  });

  const handleKeyDown = event => {
    const { key } = event;

    let newTabKey = currentTab;
    let isNextTabDisabled = true;

    switch (true) {
      case key === 'ArrowRight' && orientation === 'horizontal':
      case key === 'ArrowDown' && orientation === 'vertical':
        event.preventDefault();
        while (isNextTabDisabled) {
          if (newTabKey < totalTabs - 1) {
            newTabKey += 1;
          } else {
            newTabKey = 0;
          }
          isNextTabDisabled = children[newTabKey].props.disabled;
        }
        buttonRefs.current[newTabKey].focus();
        setCurrentTab(newTabKey);
        break;

      case key === 'ArrowLeft' && orientation === 'horizontal':
      case key === 'ArrowUp' && orientation === 'vertical':
        event.preventDefault();
        while (isNextTabDisabled) {
          if (newTabKey > 0) {
            newTabKey -= 1;
          } else {
            newTabKey = totalTabs - 1;
          }
          isNextTabDisabled = children[newTabKey].props.disabled;
        }
        buttonRefs.current[newTabKey].focus();
        setCurrentTab(newTabKey);
        break;

      default:
        break;
    }
  };

  const tabControlChildren = React.Children.map(children, (child, index) =>
    React.cloneElement(child, {
      tabKey: index
    })
  );

  return (
    <TabNavigationProvider
      value={{
        activeTab,
        setActiveTab,
        currentTab,
        buttonRefs,
        lineSide
      }}
    >
      <div
        onKeyDown={handleKeyDown}
        role="tablist"
        className={classes}
        data-motif-tab-navigation
        {...other}
      >
        {tabControlChildren}
      </div>
    </TabNavigationProvider>
  );
};

MotifTabNavigation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['alt', 'default']),
  defaultActiveKey: PropTypes.number,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  lineSide: PropTypes.oneOf(['left', 'right'])
};

export { MotifTabNavigation };
